import { z } from 'zod';

/**
 * BullMQ queue names shared by the API (producer) and the worker (consumer).
 * They live here because the two apps cannot import each other, and a typo
 * would silently split producer from consumer.
 */
export const MEDIA_DERIVATIVES_QUEUE = 'media-derivatives';
export const MEDIA_DERIVATIVES_JOB = 'derive';

/**
 * Payload of a `derive` job. `.guid()` (not `.uuid()`) to match the other
 * queue payloads — a malformed id must fail at the consumer's boundary, not
 * four calls deeper as an opaque Postgres invalid-uuid error.
 *
 * `organizationId` is what the admin queue-health view filters failures by
 * (`jobOrg()`); without it a failed derive is invisible to the tenant. It is
 * optional ONLY for backward compatibility with jobs already sitting in Redis
 * from before it was added — the API always sends it.
 */
export const MediaDerivativesJobSchema = z.object({
  mediaId: z.string().guid(),
  organizationId: z.string().guid().optional(),
});
export type MediaDerivativesJob = z.infer<typeof MediaDerivativesJobSchema>;

/**
 * Optional, top-level ids every e-mail job may carry so the worker can write a
 * `job_logs` row that is tied to a tenant and a hotel. Top-level (not nested)
 * because queue-health and JobsService read `data.organizationId` directly.
 * Optional so jobs queued by an older API during a rolling update still parse.
 */
export const EmailTraceFieldsSchema = z.object({
  organizationId: z.string().guid().optional(),
  hotelId: z.string().guid().optional(),
  batchId: z.string().guid().optional(),
  postId: z.string().guid().optional(),
  planId: z.string().guid().optional(),
});
export type EmailTrace = z.infer<typeof EmailTraceFieldsSchema>;

const TRACE_KEYS = ['organizationId', 'hotelId', 'batchId', 'postId', 'planId'] as const;
const GUID = z.string().guid();

/** Lenient read for logging: keeps valid ids, drops the rest, never throws. */
export function emailTraceOf(data: unknown): EmailTrace {
  if (typeof data !== 'object' || data === null) return {};
  const src = data as Record<string, unknown>;
  const out: EmailTrace = {};
  for (const key of TRACE_KEYS) {
    if (GUID.safeParse(src[key]).success) out[key] = src[key] as string;
  }
  return out;
}

/** Client attachments: clean (per upload) + hourly / weekly sweeps. */
export const CLIENT_ATTACHMENTS_QUEUE = 'client-attachments';
export const CLIENT_ATTACHMENT_CLEAN_JOB = 'clean';
export const CLIENT_ATTACHMENT_SWEEP_JOB = 'sweep';
export const CLIENT_ATTACHMENT_OBJECT_SWEEP_JOB = 'sweep-objects';
export const ClientAttachmentCleanJobSchema = z.object({
  attachmentId: z.string().guid(),
});
export type ClientAttachmentCleanJob = z.infer<typeof ClientAttachmentCleanJobSchema>;
